import React, { FC, useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  useTheme,
  Typography,
  Popover,
  Grid,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon, Colorize, Person as PersonIcon } from '@mui/icons-material';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PaletteIcon from '@mui/icons-material/Palette';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch, useSelector } from 'react-redux';
import { useCustomNavigate } from '../../utils/hooks/useCustomNavigate';
import strings from '../../common/Translation/Translate';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import axiosInstance from '../../utils/axios';
import { identifiers } from '../../utils/constants/identifiers';
import { privileges } from '../../utils/constants/privileges';
import { routes } from '../../utils/constants/routes';
import { URLS } from '../../utils/constants/urls';
import { setAuthentication } from '../../utils/redux';
import {
  Privilege,
  defaultLanguage,
  getOrganizationList,
  handleLanguageChange,
  organizationInfo,
  refreshToken,
  selectedOrgId,
  setOrgInfo,
  setSelectedOrganization,
  User,
} from '../../utils/redux/reducer/authentication-slice';
import { clearStorage, fetchFromStorage } from '../../utils/storage';
import { createDropdownData } from '../../utils/validators/HelperFunctions';
import LanguageDrodownValue from '../../utils/hooks/LanguageDrodownValue';
import CustomerTheme from '../AuthenticatedLayout/CustomerTheme';
import CustomTheme from '../AuthenticatedLayout/CustomTheme';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './Sidebar';

interface HeaderProps {
  onMenuClick: () => void;
  sidebarOpen: boolean;
}

const Header: FC<HeaderProps> = ({ onMenuClick, sidebarOpen }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useCustomNavigate();
  const isScreenMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const user = useSelector(User);
  const userPrivileges = useSelector(Privilege);
  const lang = useSelector(defaultLanguage);
  const org = useSelector(organizationInfo) as any;
  const selectedOrganization = useSelector(selectedOrgId);
  const initOrg = fetchFromStorage(identifiers.organization) as any;
  const languageData = LanguageDrodownValue();

  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLElement | null>(null);
  const [isCustomThemeDialogOpen, setIsCustomThemeDialogOpen] = useState<boolean>(false);
  const [isCustomerThemeDialogOpen, setIsCustomerThemeDialogOpen] = useState<boolean>(false);
  const [organizationList, setOrganizationList] = useState<[]>();
  const orgSwitcherRef = useRef<HTMLDivElement>(null);

  const canSwitchOrganization = (organizationList || []).length > 1;

  const openOrganizationMenu = () => {
    if (!canSwitchOrganization) return;
    const control = orgSwitcherRef.current?.querySelector(
      '.select [class*="control"]',
    ) as HTMLElement | null;
    control?.dispatchEvent(
      new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );
  };

  const openOrganizationSwitcher = (event: React.MouseEvent<HTMLElement>) => {
    if (!canSwitchOrganization) return;

    const target = event.target as HTMLElement;
    if (target.closest('input') || target.closest('.select') || target.closest('[class*="control"]')) {
      return;
    }

    openOrganizationMenu();
  };

  const openPopOver = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleClose = () => setPopoverAnchorEl(null);

  const isPopoverOpen = Boolean(popoverAnchorEl);

  const openCustomThemeDialog = () => {
    handleClose();
    setIsCustomThemeDialogOpen(true);
  };

  const openCustomerThemeDialog = () => {
    handleClose();
    setIsCustomerThemeDialogOpen(true);
  };

  const handleLogout = async () => {
    const body: { refreshToken: string | null } = {
      refreshToken: fetchFromStorage(identifiers.refresh_token),
    };
    try {
      const { status }: { status: number } = await axiosInstance.post(URLS.logout, body);
      if (status === 200) {
        clearStorage();
        dispatch(setAuthentication(null));
      }
    } catch (error: any) {
      clearStorage();
      dispatch(setAuthentication(null));
    }
  };

  const navigateToProfile = () => navigate(routes.profile);

  useEffect(() => {
    if (initOrg !== '*') {
      setOrganizationList(org?.organizations);
      dispatch(setSelectedOrganization(org?.selectedOrganization));
    }
  }, [org, initOrg, dispatch]);

  useEffect(() => {
    (async () => {
      if (initOrg !== '*') dispatch(getOrganizationList() as any);
    })();
  }, [dispatch, initOrg]);

  const handleDropdownChange = async (value: any, field: string) => {
    if (field === 'org') {
      try {
        const { status } = await axiosInstance.post(URLS.setOrganizations, { id: value.value });
        if (status === 200) {
          await dispatch(refreshToken() as any);
          dispatch(setSelectedOrganization(value));
          dispatch(setOrgInfo({ ...org, selectedOrganization: value }));
          navigate(routes.bookings);
          setTimeout(() => window.location.reload(), 50);
        }
      } catch (error: any) {
        console.error('Error setting organization:', error);
      }
    } else {
      try {
        dispatch(handleLanguageChange(value) as any);
        initOrg !== '*' && navigate(routes.bookings);
      } catch (error: any) {
        console.error('Error changing language:', error);
      }
    }
  };

  return (
    <>
      <CustomTheme open={isCustomThemeDialogOpen} setOpen={setIsCustomThemeDialogOpen} />
      <CustomerTheme open={isCustomerThemeDialogOpen} setOpen={setIsCustomerThemeDialogOpen} />

      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          zIndex: theme.zIndex.drawer - 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          left: {
            xs: 0,
            md: sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
          },
          width: {
            xs: '100%',
            md: `calc(100% - ${sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH}px)`,
          },
          transition: theme.transitions.create(['left', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}>
        <Toolbar
          sx={{
            pl: { xs: 1, sm: 0.75 },
            pr: { xs: 2, sm: 2 },
            minHeight: '64px !important',
            gap: 1,
            overflow: 'visible',
          }}>
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ ml: 0, mr: 0, p: 1 }}>
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {/* Organization switcher */}
          {initOrg !== '*' && (
            <Box
              ref={orgSwitcherRef}
              role="button"
              tabIndex={canSwitchOrganization ? 0 : -1}
              aria-label="Switch organization"
              onClick={openOrganizationSwitcher}
              onKeyDown={event => {
                if (canSwitchOrganization && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  openOrganizationMenu();
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                minWidth: { xs: 200, sm: 260, md: 280 },
                maxWidth: { xs: 240, sm: 300, md: 320 },
                flex: '0 1 auto',
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                border: '1px solid #E4E7EC',
                bgcolor: '#FFFFFF',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                cursor: canSwitchOrganization ? 'pointer' : 'default',
                outline: 'none',
                '&:hover': canSwitchOrganization
                  ? {
                      borderColor: '#D0D5DD',
                      boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
                    }
                  : undefined,
                '&:focus-visible': {
                  borderColor: '#98A2B3',
                  boxShadow: '0 0 0 3px rgba(152, 162, 179, 0.2)',
                },
                '& .org-dropdown': {
                  flex: 1,
                  minWidth: 0,
                  pointerEvents: canSwitchOrganization ? 'auto' : 'none',
                },
                '& .org-dropdown .dropdown': {
                  width: '100%',
                },
              }}>
              <HomeWorkOutlinedIcon
                sx={{
                  fontSize: 22,
                  color: '#344054',
                  flexShrink: 0,
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 0.25,
                }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#98A2B3',
                    lineHeight: 1.1,
                    pointerEvents: 'none',
                  }}>
                  {strings.organization_title_list}
                </Typography>
                <Box
                  sx={{
                    minWidth: 0,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    height: 22,
                    '& .MuiFormControl-root': {
                      display: 'flex',
                      alignItems: 'center',
                      height: 22,
                      minHeight: 22,
                      m: 0,
                    },
                  }}>
                  <MultiSelect
                    placeholder={strings.organization_dropdown_placeholder_text}
                    field="org"
                    isSearchable={true}
                    isClearable={false}
                    className="org-dropdown org-dropdown--header"
                    options={createDropdownData(organizationList, ['id', 'name']) || []}
                    onChange={handleDropdownChange}
                    inputValue={selectedOrganization}
                    id="org"
                    controlHeight={22}
                    disabled={!canSwitchOrganization}
                    style={{
                      control: (provided: any) => ({
                        ...provided,
                        border: 'none',
                        boxShadow: 'none',
                        background: 'transparent',
                        minHeight: 22,
                        height: 22,
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          border: 'none',
                        },
                      }),
                      valueContainer: (provided: any) => ({
                        ...provided,
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        padding: 0,
                        margin: 0,
                      }),
                      singleValue: (provided: any) => ({
                        ...provided,
                        position: 'static',
                        transform: 'none',
                        maxWidth: '100%',
                        fontSize: 14,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        margin: 0,
                        color: '#101828',
                      }),
                      placeholder: (provided: any) => ({
                        ...provided,
                        position: 'static',
                        transform: 'none',
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        margin: 0,
                        color: '#667085',
                      }),
                      indicatorsContainer: () => ({
                        display: 'none',
                      }),
                      menu: (provided: any) => ({
                        ...provided,
                        minWidth: 420,
                        width: 'max(420px, 100%)',
                        fontSize: 14,
                        right: 0,
                        left: 'auto',
                      }),
                      menuList: (provided: any) => ({
                        ...provided,
                        maxHeight: 360,
                        padding: 6,
                      }),
                      option: (provided: any) => ({
                        ...provided,
                        padding: '12px 14px',
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: 1.35,
                      }),
                    }}
                  />
                </Box>
              </Box>
              {canSwitchOrganization ? (
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 20,
                    color: '#344054',
                    flexShrink: 0,
                    pointerEvents: 'none',
                  }}
                />
              ) : null}
            </Box>
          )}

          {/* Language dropdown */}
          {isScreenMdUp && languageData && languageData.length > 1 && (
            <MultiSelect
              isSearchable={false}
              isClearable={false}
              placeholder={strings.language_dropdown_placeholder_text}
              field="language"
              className="language-dropdown"
              options={createDropdownData(languageData, ['value', 'text']) || []}
              onChange={handleDropdownChange}
              inputValue={lang}
              id="language"
            />
          )}

          {/* Avatar — Profile / Themes / Logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <IconButton
              size="small"
              onClick={openPopOver}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}>
              <Avatar
                sx={{
                  backgroundColor: theme.palette.primary.main,
                }}>
                {user?.firstName?.[0] || 'JD'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box p={2} minWidth={180}>
          <Grid container direction="column" spacing={0.5}>
            <Grid item>
              <IconButton
                onClick={() => {
                  navigateToProfile();
                  handleClose();
                }}
                disableRipple
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography ml={1}>Profile</Typography>
              </IconButton>
            </Grid>
            {initOrg !== '*' && userPrivileges.includes(privileges.edit_theme) && (
              <>
                <Grid item>
                  <IconButton
                    onClick={() => {
                      openCustomerThemeDialog();
                      handleClose();
                    }}
                    disableRipple
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}>
                    <Colorize sx={{ mr: 1 }} />
                    <Typography ml={1}>Customer Theme</Typography>
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton
                    onClick={() => {
                      openCustomThemeDialog();
                      handleClose();
                    }}
                    disableRipple
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}>
                    <PaletteIcon sx={{ mr: 1 }} />
                    <Typography ml={1}>Admin Theme</Typography>
                  </IconButton>
                </Grid>
              </>
            )}
            <Grid item>
              <IconButton
                onClick={() => {
                  handleLogout();
                  handleClose();
                }}
                disableRipple
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}>
                <LogoutIcon sx={{ mr: 1 }} />
                <Typography ml={1}>{strings.logoutText || 'Logout'}</Typography>
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </>
  );
};

export default Header;
